import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import AdminMediaLibrary from './AdminMediaLibrary';

const AdminLectureForm = ({ courseId, lecture, onSave, onCancel, nextOrder }) => {
    const [formData, setFormData] = useState({
        title: '',
        lecture_type: 'video',
        content: ''
    });
    const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);

    useEffect(() => {
        if (lecture) {
            setFormData({
                title: lecture.title || '',
                lecture_type: lecture.lecture_type || 'video',
                content: lecture.content || ''
            });
        }
    }, [lecture]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'lecture_type') {
            setFormData(prev => ({ ...prev, [name]: value, content: '' }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleContentChange = (event, editor) => {
        setFormData(prev => ({ ...prev, content: editor.getData() }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('app_token');
        const headers = { Authorization: `Bearer ${token}` };

        try {
            if (lecture) {
                await axios.put(`/api/admin/lectures/${lecture.id}`, formData, { headers });
            } else {
                const payload = { ...formData, lecture_order: nextOrder };
                const response = await axios.post(`/api/admin/courses/${courseId}/lectures`, payload, { headers });
                
                // If a new test is created, also create the test shell in the DB
                if (formData.lecture_type === 'test') {
                    const newLectureId = response.data.id;
                    await axios.post(`/api/admin/lectures/${newLectureId}/test`, 
                        { title: 'New Test', passing_score: 70, lectureId: newLectureId },
                        { headers }
                    );
                }
            }
            onSave();
        } catch (error) {
            alert('Failed to save lecture. Please ensure your database has been updated.');
            console.error("Error saving lecture:", error);
        }
    };

    // Render the content input based on the selected lecture type
    const renderContentInput = () => {
        switch (formData.lecture_type) {
            case 'video':
                return (
                    <div className="media-input-group">
                        <input type="text" name="content" value={formData.content} readOnly placeholder="Select a video..."/>
                        <button type="button" className="btn-secondary" onClick={() => setIsMediaLibraryOpen(true)}>Select Video</button>
                    </div>
                );
            case 'text':
                return <CKEditor editor={ClassicEditor} data={formData.content} onChange={handleContentChange} />;
            case 'test':
                return <p>Test questions and settings are managed after the lecture is created. Save this lecture to begin building the test.</p>;
            default:
                return null;
        }
    };

    return (
        <>
            {isMediaLibraryOpen && (
                <AdminMediaLibrary
                    onSelect={(url) => {
                        setFormData(prev => ({ ...prev, content: url }));
                        setIsMediaLibraryOpen(false);
                    }}
                    onCancel={() => setIsMediaLibraryOpen(false)}
                    s3Prefix="hj1/public/assets/videos/"
                />
            )}
            <div className="admin-modal-overlay">
                <div className="admin-modal-content">
                    <h2>{lecture ? 'Edit Lecture' : 'Add New Lecture'}</h2>
                    <form onSubmit={handleSubmit} className="admin-form">
                        <div className="form-group">
                            <label>Title</label>
                            <input type="text" name="title" value={formData.title} onChange={handleChange} required />
                        </div>
                        <div className="form-group">
                            <label>Type</label>
                            <select name="lecture_type" value={formData.lecture_type} onChange={handleChange}>
                                <option value="video">Video</option>
                                <option value="text">Text</option>
                                <option value="test">Test</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Content</label>
                            {renderContentInput()}
                        </div>
                        <div className="form-actions">
                            <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
                            <button type="submit" className="btn-primary">Save Lecture</button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default AdminLectureForm;
